import { useState, useMemo, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { PageHero, CtaSection, ProjectGrid, Pagination } from '../../components'
import { useProjects } from '../../features/projects/hooks/useProjects'
import type { ProjectDetail } from '../../features/projects/types'
import useDocumentTitle from '../../hooks/useDocumentTitle'
import './CongTrinhThietKe.css'

const BREADCRUMBS = [
  { label: 'Trang chủ', path: '/' },
  { label: 'Công trình thiết kế' },
]

// Lưu trữ trang phân trang hiện tại của trang tổng hợp bằng sessionStorage để tránh bị reset về trang 1
const getCachedGeneralPage = (): number => {
  try {
    const saved = sessionStorage.getItem('page_cache_general_design')
    return saved ? parseInt(saved, 10) : 1
  } catch (e) {
    return 1
  }
}

const setCachedGeneralPage = (page: number) => {
  try {
    sessionStorage.setItem('page_cache_general_design', page.toString())
  } catch (e) {
    // ignore
  }
}

// Hàm trộn các danh sách dự án xen kẽ nhau (Interleave) để tạo độ xáo trộn nhưng ổn định khi phân trang
const interleaveProjects = (lists: ProjectDetail[][]): ProjectDetail[] => {
  const mixed: ProjectDetail[] = []
  const maxLen = Math.max(...lists.map((l) => l.length))
  for (let i = 0; i < maxLen; i++) {
    for (const list of lists) {
      if (list[i]) {
        mixed.push(list[i])
      }
    }
  }
  return mixed
}

const removeVietnameseTones = (str: string): string => {
  str = str.replace(/à|á|ạ|ả|ã|â|ầ|ấ|ậ|ẩ|ẫ|ă|ằ|ắ|ặ|ẳ|ẵ/g, "a");
  str = str.replace(/è|é|ẹ|ẻ|ẽ|ê|ề|ế|ệ|ể|ễ/g, "e");
  str = str.replace(/ì|í|ị|ỉ|ĩ/g, "i");
  str = str.replace(/ò|ó|ọ|ỏ|õ|ô|ồ|ố|ộ|ổ|ỗ|ơ|ờ|ớ|ợ|ở|ỡ/g, "o");
  str = str.replace(/ù|ú|ụ|ủ|ũ|ư|ừ|ứ|ự|ử|ữ/g, "u");
  str = str.replace(/ỳ|ý|ỵ|ỷ|ỹ/g, "y");
  str = str.replace(/đ/g, "d");
  str = str.replace(/À|Á|Ạ|Ả|Ã|Â|Ầ|Ấ|Ậ|Ẩ|Ẫ|Ă|Ằ|Ắ|Ặ|Ẳ|Ẵ/g, "A");
  str = str.replace(/È|É|Ẹ|Ẻ|Ẽ|Ê|Ề|Ế|Ệ|Ể|Ễ/g, "E");
  str = str.replace(/Ì|Í|Ị|Bỉ|Ĩ/g, "I");
  str = str.replace(/Ò|Ó|Ọ|Ỏ|Õ|Ô|Ồ|Ố|Ộ|Ổ|Ỗ|Ơ|Ờ|Ớ|Ợ|Ở|Ỡ/g, "O");
  str = str.replace(/Ù|Ú|Ụ|Ủ|Ũ|Ư|Ừ|Ứ|Ự|Ử|Ữ/g, "U");
  str = str.replace(/Ỳ|Ý|Ỵ|Ỷ|Ỹ/g, "Y");
  str = str.replace(/Đ/g, "D");
  str = str.replace(/\u0300|\u0301|\u0309|\u0303|\u0323/g, "");
  str = str.replace(/\u02C6|\u0306|\u031B/g, "");
  str = str.replace(/ + /g, " ");
  return str.trim();
}

const CongTrinhThietKePage = () => {
  useDocumentTitle('Công Trình Thiết Kế - MAXCONST')
  const [currentPage, setCurrentPage] = useState<number>(() => getCachedGeneralPage())
  const [searchParams] = useSearchParams()
  const searchQuery = searchParams.get('q') || ''

  // Lưu thông tin trang cuối cùng đã ghé thăm để làm Breadcrumb động trên trang chi tiết
  useState(() => {
    try {
      sessionStorage.setItem('lastVisitedList', '/cong-trinh-thiet-ke')
      sessionStorage.setItem('lastVisitedListName', 'Công trình thiết kế')
    } catch (e) {
      // ignore
    }
  })

  // Fetch tất cả 5 danh mục thiết kế từ API/cache
  const cap4 = useProjects({
    category: 'cap4',
    pages: 10,
  })

  const tang2 = useProjects({
    category: '2tang',
    pages: 10,
  })

  const nhieuTang = useProjects({
    category: 'nhieu-tang',
    pages: 10,
  })

  const nhaPho = useProjects({
    category: 'nha-pho',
    pages: 10,
  })

  const bietThu = useProjects({
    category: 'biet-thu',
    pages: 10,
  })

  // Trộn ổn định danh sách tất cả các chuyên mục để xáo đều
  const displayProjects = useMemo(() => {
    return interleaveProjects([
      cap4.projects,
      tang2.projects,
      nhieuTang.projects,
      nhaPho.projects,
      bietThu.projects,
    ])
  }, [
    cap4.projects,
    tang2.projects,
    nhieuTang.projects,
    nhaPho.projects,
    bietThu.projects,
  ])

  // Lọc dự án theo từ khóa tìm kiếm (search query)
  const filteredProjects = useMemo(() => {
    if (!searchQuery.trim()) return displayProjects
    const query = searchQuery.toLowerCase().trim()
    const normalizedQuery = removeVietnameseTones(query)
    return displayProjects.filter((p) => {
      const titleLower = p.title.toLowerCase()
      const normalizedTitle = removeVietnameseTones(titleLower)
      return titleLower.includes(query) || normalizedTitle.includes(normalizedQuery)
    })
  }, [displayProjects, searchQuery])

  // Reset page về 1 nếu từ khóa tìm kiếm thay đổi
  useMemo(() => {
    setCurrentPage(1)
    setCachedGeneralPage(1)
  }, [searchQuery])

  // Tính toán phân trang
  const ITEMS_PER_PAGE = 24
  const totalCount = filteredProjects.length
  const totalPages = Math.max(1, Math.ceil(totalCount / ITEMS_PER_PAGE))
  const safeCurrentPage = Math.min(currentPage, totalPages)
  const startIndex = (safeCurrentPage - 1) * ITEMS_PER_PAGE
  const endIndex = Math.min(startIndex + ITEMS_PER_PAGE, totalCount)

  const currentProjects = useMemo(() => {
    return filteredProjects.slice(startIndex, endIndex)
  }, [filteredProjects, startIndex, endIndex])

  // Handlers phân trang
  const handlePageChange = useCallback((page: number) => {
     const valid = Math.max(1, Math.min(page, totalPages))
     setCurrentPage(valid)
     setCachedGeneralPage(valid) // Lưu lại trang hiện tại vào bộ nhớ đệm
     window.scrollTo({ top: 0, behavior: 'smooth' })
   }, [totalPages])

  const handlePrevPage = useCallback(() => {
    handlePageChange(safeCurrentPage - 1)
  }, [safeCurrentPage, handlePageChange])

  const handleNextPage = useCallback(() => {
    handlePageChange(safeCurrentPage + 1)
  }, [safeCurrentPage, handlePageChange])

  // Trả về danh sách số trang dạng cửa sổ trượt (sliding window) chuyên nghiệp
  const getPageNumbers = useCallback((): (number | '...')[] => {
    const pages: (number | '...')[] = []
    
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i)
      }
      return pages
    }

    pages.push(1)

    const leftBound = Math.max(2, safeCurrentPage - 2)
    const rightBound = Math.min(totalPages - 1, safeCurrentPage + 2)

    if (leftBound > 2) {
      pages.push('...')
    }

    let start = leftBound
    let end = rightBound
    
    if (safeCurrentPage <= 4) {
      start = 2
      end = 5
    } else if (safeCurrentPage >= totalPages - 3) {
      start = totalPages - 4
      end = totalPages - 1
    }

    for (let i = start; i <= end; i++) {
      if (i > 1 && i < totalPages) {
        pages.push(i)
      }
    }

    if (end < totalPages - 1) {
      pages.push('...')
    }

    pages.push(totalPages)

    return pages
  }, [safeCurrentPage, totalPages])

  const globalLoading =
    cap4.loading ||
    tang2.loading ||
    nhieuTang.loading ||
    nhaPho.loading ||
    bietThu.loading

  const globalError =
    cap4.error || tang2.error || nhieuTang.error || nhaPho.error || bietThu.error

  return (
    <div className="cong-trinh-thiet-ke-page">
      {/* Hero */}
      <PageHero
        tag="Hồ Sơ Thiết Kế"
        title="Công Trình Thiết Kế MAXCONST"
        titleHighlight="MAXCONST"
        description="Khám phá bộ sưu tập hàng trăm mẫu thiết kế kiến trúc phong cách hiện đại, tân cổ điển đẳng cấp"
        breadcrumbs={BREADCRUMBS}
      />

      {/* Content Layout */}
      <div className="container section">
        {searchQuery.trim() && !globalLoading && !globalError && (
          <div className="search-results-info">
            <div className="search-results-text">
              Kết quả tìm kiếm cho: <span>"{searchQuery}"</span>
            </div>
            <div className="search-results-count">
              Tìm thấy <strong>{totalCount}</strong> dự án phù hợp
            </div>
          </div>
        )}

        <ProjectGrid
          projects={currentProjects}
          loading={globalLoading}
          error={globalError}
          emptyMessage={`Không tìm thấy dự án nào phù hợp với từ khóa "${searchQuery}"`}
        />

        {!globalLoading && !globalError && totalPages > 1 && (
          <Pagination
            currentPage={safeCurrentPage}
            totalPages={totalPages}
            totalCount={totalCount}
            startIndex={startIndex}
            endIndex={endIndex}
            getPageNumbers={getPageNumbers}
            onPageChange={handlePageChange}
            onPrev={handlePrevPage}
            onNext={handleNextPage}
          />
        )}
      </div>

      {/* CTA */}
      <CtaSection
        title="Bạn Đăng Tìm Giải Pháp Cho Ngôi Nhà Của Mình?"
        titleHighlight="Giải Pháp"
        description="Đăng ký tư vấn miễn phí ngay để đội ngũ kiến trúc sư Maxconst hỗ trợ bạn nhanh nhất."
      />
    </div>
  )
}

export default CongTrinhThietKePage
